import styled, { css, keyframes } from 'styled-components';
import { fadeUp } from './AuthLayout.styles';

const ping = keyframes`
  75%, 100% { transform: scale(2); opacity: 0; }
`;

const pulse = keyframes`
  50% { opacity: 0.5; }
`;

const miniScan = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(300%); }
`;

const animateFadeUp = css`
  animation: ${fadeUp} 0.5s ease-out both;
`;

export const PreviewRoot = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: var(--auth-gradient-soft);
  }
`;

export const PreviewInner = styled.div`
  position: relative;
  width: 100%;
  max-width: 512px;
  padding: 0 32px;
`;

export const PreviewHeadline = styled.h2`
  margin: 0 0 32px;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: var(--auth-foreground);
  text-transform: none;

  span {
    color: color-mix(in oklab, var(--auth-muted-foreground) 60%, transparent);
  }
`;

export const PreviewCard = styled.div`
  overflow: hidden;
  border-radius: 20px;
  border: 1px solid color-mix(in oklab, var(--auth-border) 50%, transparent);
  background: color-mix(in oklab, var(--auth-background) 70%, transparent);
  box-shadow:
    0 1px 2px rgba(16, 24, 40, 0.04),
    0 12px 40px -12px rgba(16, 24, 40, 0.12);
  backdrop-filter: blur(24px);
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid color-mix(in oklab, var(--auth-border) 40%, transparent);
`;

export const SessionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.025em;
  color: var(--auth-foreground);

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 9999px;
    background-image: var(--auth-gradient-houdiny);
  }
`;

export const LiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  color: var(--auth-muted-foreground);

  .pulse {
    position: relative;
    display: flex;
    width: 6px;
    height: 6px;
  }

  .ring {
    position: absolute;
    display: inline-flex;
    width: 100%;
    height: 100%;
    border-radius: 9999px;
    background: rgba(16, 185, 129, 0.6);
    animation: ${ping} 1s cubic-bezier(0, 0, 0.2, 1) infinite;
  }

  .core {
    position: relative;
    display: inline-flex;
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    background: #10b981;
  }
`;

export const Conversation = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 12px;
  min-height: 208px;
  padding: 20px;
`;

export const BubbleRow = styled.div`
  display: flex;
  justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
`;

export const BubbleBody = styled.div`
  max-width: 85%;
  padding: 8px 14px;
  border-radius: 22px;
  font-size: 13px;
  line-height: 1.55;
  letter-spacing: -0.025em;

  ${({ $isUser }) =>
    $isUser
      ? css`
          border-bottom-right-radius: 12px;
          background: var(--auth-foreground);
          color: var(--auth-background);
        `
      : css`
          border-bottom-left-radius: 12px;
          background: color-mix(in oklab, var(--auth-muted) 60%, transparent);
          color: var(--auth-foreground);
        `}
`;

export const Caret = styled.span`
  display: inline-block;
  width: 1.5px;
  height: 12px;
  margin-left: 2px;
  vertical-align: middle;
  transform: translateY(2px);
  background: currentColor;
  animation: ${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`;

export const Composer = styled.div`
  padding: 0 20px 20px;

  > div {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    border-radius: 9999px;
    border: 1px solid color-mix(in oklab, var(--auth-border) 60%, transparent);
    background: var(--auth-background);
  }

  .placeholder {
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 13px;
    color: color-mix(in oklab, var(--auth-muted-foreground) 70%, transparent);
  }

  .send {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 9999px;
    background: var(--auth-foreground);
    color: var(--auth-background);

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

/* ---------------- Mini campaign builder ---------------- */

export const Builder = styled.div`
  padding: 16px 20px;
  border-top: 1px solid color-mix(in oklab, var(--auth-border) 40%, transparent);
  background: color-mix(in oklab, var(--auth-muted) 20%, transparent);
`;

export const Rail = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const RailStep = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 6px;
`;

export const RailDot = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  transition: all 0.5s ease;

  svg {
    width: 10px;
    height: 10px;
  }

  ${({ $active, $done }) => {
    if ($active)
      return css`
        background-image: var(--auth-gradient-houdiny);
        color: #fff;
        box-shadow: var(--auth-shadow-card);
      `;
    if ($done)
      return css`
        background: color-mix(in oklab, var(--auth-foreground) 85%, transparent);
        color: var(--auth-background);
      `;
    return css`
      border: 1px solid var(--auth-border);
      background: var(--auth-background);
      color: color-mix(in oklab, var(--auth-muted-foreground) 50%, transparent);
    `;
  }}
`;

export const RailLabel = styled.span`
  display: none;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: -0.025em;
  color: ${({ $active }) =>
    $active ? 'var(--auth-foreground)' : 'color-mix(in oklab, var(--auth-muted-foreground) 60%, transparent)'};

  @media (min-width: 640px) {
    display: inline;
  }
`;

export const RailLine = styled.span`
  flex: 1;
  height: 1px;
  transition: background-color 0.5s ease;
  background: ${({ $done }) =>
    $done
      ? 'color-mix(in oklab, var(--auth-foreground) 25%, transparent)'
      : 'color-mix(in oklab, var(--auth-border) 70%, transparent)'};
`;

export const StageCanvas = styled.div`
  ${animateFadeUp}
  margin-top: 12px;
  min-height: 88px;
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const Chip = styled.span`
  ${animateFadeUp}
  padding: 4px 10px;
  border-radius: 9999px;
  border: 1px solid color-mix(in oklab, var(--auth-border) 70%, transparent);
  background: var(--auth-background);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: -0.025em;
  color: var(--auth-foreground);
`;

export const ScanList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ScanRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > span:first-child {
    width: 88px;
    flex-shrink: 0;
    font-size: 11px;
    color: var(--auth-muted-foreground);
  }
`;

export const ScanTrack = styled.span`
  position: relative;
  flex: 1;
  height: 6px;
  overflow: hidden;
  border-radius: 9999px;
  background: color-mix(in oklab, var(--auth-border) 60%, transparent);

  span {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 33.333%;
    border-radius: 9999px;
    background-image: var(--auth-gradient-houdiny);
    animation: ${miniScan} 1.6s ease-in-out ${({ $delay }) => $delay}s infinite;
  }
`;

export const ScanNote = styled.div`
  padding-top: 2px;
  font-size: 11px;
  color: var(--auth-muted-foreground);
`;

export const LeadList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const LeadRow = styled.div`
  ${animateFadeUp}
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-radius: 14px;
  border: 1px solid color-mix(in oklab, var(--auth-border) 60%, transparent);
  background: var(--auth-background);
`;

export const LeadAvatar = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  background-image: var(--auth-gradient-houdiny);
  font-size: 9px;
  font-weight: 600;
  color: #fff;
`;

export const LeadName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: -0.025em;
  color: var(--auth-foreground);

  span {
    margin-left: 4px;
    font-weight: 400;
    color: var(--auth-muted-foreground);
  }
`;

export const ScoreTrack = styled.span`
  width: 40px;
  height: 6px;
  overflow: hidden;
  border-radius: 9999px;
  background: color-mix(in oklab, var(--auth-border) 60%, transparent);

  span {
    display: block;
    height: 100%;
    border-radius: 9999px;
    background-image: var(--auth-gradient-houdiny);
  }
`;

export const ScoreValue = styled.span`
  width: 24px;
  text-align: right;
  font-size: 10px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--auth-foreground);
`;

export const SequenceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const SequenceStep = styled.div`
  ${animateFadeUp}
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 58px;
  padding: 6px 4px;
  border-radius: 14px;
  border: 1px solid color-mix(in oklab, var(--auth-border) 60%, transparent);
  background: var(--auth-background);

  svg {
    width: 12px;
    height: 12px;
    color: var(--auth-primary);
  }

  span {
    text-align: center;
    font-size: 9px;
    font-weight: 500;
    line-height: 1.25;
    letter-spacing: -0.025em;
    color: var(--auth-muted-foreground);
  }
`;

export const SequenceConnector = styled.span`
  width: 6px;
  height: 1px;
  background: color-mix(in oklab, var(--auth-border) 70%, transparent);
`;

export const LaunchCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 18px;
  border: 1px solid color-mix(in oklab, var(--auth-border) 60%, transparent);
  background: var(--auth-background);

  > div {
    min-width: 0;
  }

  strong {
    display: block;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: -0.025em;
    color: var(--auth-foreground);
  }

  p {
    margin: 0;
    font-size: 10.5px;
    color: var(--auth-muted-foreground);
  }
`;

export const LaunchIcon = styled.span`
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  background-image: var(--auth-gradient-houdiny);
  color: #fff;

  .ping {
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    background: color-mix(in oklab, var(--auth-houdiny-from) 40%, transparent);
    animation: ${ping} 1s cubic-bezier(0, 0, 0.2, 1) infinite;
  }

  svg {
    position: relative;
    width: 16px;
    height: 16px;
  }
`;
